import type { NavigateTarget, ParentMessagingPort } from '@s2w/core';
import { buildCloseGameMessage, buildNavigateMessage } from './messaging.js';

export interface PostMessageTarget {
  postMessage(message: unknown, targetOrigin: string): void;
}

/**
 * The slice of `window` the send path reads: the frame to post to, and the
 * window itself, so the two can be compared.
 */
export interface ParentMessagingWindow extends PostMessageTarget {
  readonly parent: PostMessageTarget;
}

export interface CaesarsParentMessagingOptions {
  readonly window: ParentMessagingWindow;
  /** `VITE_PARENT_ORIGIN`: the sportsbook page that frames the Game. */
  readonly targetOrigin: string;
}

export class CaesarsParentMessaging implements ParentMessagingPort {
  private readonly window: ParentMessagingWindow;
  private readonly targetOrigin: string;

  public constructor(options: CaesarsParentMessagingOptions) {
    this.window = options.window;
    this.targetOrigin = options.targetOrigin;
  }

  public closeGame(): void {
    this.post(buildCloseGameMessage());
  }

  public navigate(target: NavigateTarget): void {
    this.post(buildNavigateMessage(target));
  }

  /**
   * Where a message is addressed depends on whether anything frames the Game.
   *
   * Framed, `parent` is the sportsbook page: the configured origin names the
   * receiver and is the one the browser should check.
   *
   * Unframed, `parent` is this document — the shape a native container's
   * webview gives the Game, where nothing frames it. Addressing that post to
   * the sportsbook's origin asks the browser to deliver to an origin the
   * receiver does not have, so it drops the message without dispatching it and
   * without throwing, and the header Close does nothing (CAE-1655). Posting to
   * this document instead lets a host listener injected at document start hear
   * it — the self-post CAE-1321 built for Pick'ems and CAE-1345 wired into
   * Squads.
   *
   * The gate is `!framed` alone. Requiring a detectable container was the
   * mistake CAE-1345 records: a host that registers no named channel is
   * invisible to the page, and those hosts are the whole point of this path.
   *
   * It classifies topology, not container: a Game opened with `window.open`
   * also has no parent, and its real receiver is `opener`, which this does not
   * address. No deployment launches the Game that way today, and guessing at
   * one would post to a window we have not seen.
   */
  private post(message: unknown): void {
    const framed = this.window.parent !== this.window;
    if (framed) {
      this.window.parent.postMessage(message, this.targetOrigin);
      return;
    }
    // The self-post names no origin, which is the one place this parts company
    // with Pick'ems' `createSelfPostParentBridge` (it pins `location.origin`).
    //
    // A narrower origin here buys no narrower audience, as that bridge's own
    // comment concedes: the envelope is dispatched as a `message` event on this
    // document, so every listener on this page hears it either way — the host's,
    // and anything else the container injected. The origin only decides whether
    // the browser dispatches at all. Pick'ems pins it under NFR-14, which is
    // that build's requirement and not this one's, and which CAE-1070 relaxed to
    // an explicit `"*"` anyway, matching over-under — the sibling running the
    // wildcard in production, in the Operator's iOS container, since launch.
    //
    // What the wildcard buys is the container with no pinnable origin. A
    // `WKWebView` loaded by `loadHTMLString(_, baseURL: nil)` reports
    // `location.origin` as the literal `"null"` (measured on CAE-1321), and
    // postMessage takes `"*"`, `"/"` or a parseable URL — so pinning it there
    // throws `SyntaxError` out of the click instead of closing the Game.
    //
    // Addressed to the window, not to `parent` — the same object here, but it
    // is the document this is deliberately posting to. Were `framed` ever
    // widened (an inaccessible cross-origin parent read as unframed, say),
    // going through `parent` would start broadcasting to a third party at `*`.
    this.window.postMessage(message, '*');
  }
}
